import { Link, Outlet, createFileRoute, redirect, useRouterState } from '@tanstack/react-router'

import { AppShell } from '@/components/layout/app-shell'
import { ModuleDeniedNotice, NoModulesNotice } from '@/components/layout/module-gate'
import { Button } from '@/components/ui/button'
import { currentUserQueryOptions } from '@/lib/auth'
import { moduleGateFor } from '@/lib/nav'

/**
 * 登录门禁 + 模块门禁 + 控制台外壳(pathless layout route,不占 URL 段)。
 *
 * beforeLoad 在渲染前问一次 GET /api/v1/auth/me:
 *  - 未登录 → redirect 到 /login?next=<当前完整路径>,冷加载任意深链都能登完回原处;
 *  - 已登录 → 用户对象 + **模块清单**进 route context,子路由 / 顶栏 / 左导航直接取,不再重复请求。
 *
 * ★feat-008 S7:模块清单在 **beforeLoad** 就取到手(`ensureQueryData`),而不是在组件里等一个
 * loading 态 —— 无权模块的深链冷加载因此**直接**落引导页,不会先闪半帧错误态或半张页面。
 * 门禁收在这一层(而不是逐个域路由的 beforeLoad 里散写八份):`_authed` 是全部业务路由的
 * 唯一父级,一处判定即全覆盖,新增域路由自动纳入、不会漏挂。
 *
 * ★feat-008 W12(cf 呈报 3 落地):身份与模块清单来自**同一次**请求。此前这里连问两条
 * (登录门禁走 `currentUserQueryOptions`、清单走生成 hook 的键),不只是冷加载多打一次
 * `/auth/me`,还得为「会话恰好在两条之间过期」写一段 401 兜底;两键归一后那段兜底连同
 * `isApiError` 的 import 一起消失 —— 401 在这一条里就是 `null`,直接走登录跳转。
 *
 * 判定用「渲染引导页」而不是 `redirect`:静默改 URL 会让人以为自己点错了链接,而这里真正
 * 要传达的是「你没有这个模块」。引导页把**去处**给足(第一个可达模块),同时守住设计书 §5.2
 * 的两条红线:不空白屏、不死循环重定向。
 */
export const Route = createFileRoute('/_authed')({
  beforeLoad: async ({ context, location }) => {
    const user = await context.queryClient.ensureQueryData(currentUserQueryOptions())
    if (!user) throw redirect({ to: '/login', search: { next: location.href } })

    return { user, modules: user.modules }
  },
  component: AuthedLayout,
})

function AuthedLayout() {
  const { user, modules } = Route.useRouteContext()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const gate = moduleGateFor(pathname, modules)

  return (
    <AppShell user={user} modules={modules}>
      {gate.kind === 'allow' && <Outlet />}
      {gate.kind === 'empty' && <NoModulesNotice />}
      {gate.kind === 'denied' && (
        <ModuleDeniedNotice
          action={
            <Button asChild>
              <Link to={gate.fallback.to}>去{gate.fallback.label}</Link>
            </Button>
          }
        />
      )}
    </AppShell>
  )
}
